import { useEffect, useRef } from "react";
import { Modal } from "./ui";
import { Icon } from "./Icons";
import { gsap } from "../lib/anim";
import { fmtDate } from "../lib/format";
import { engagedDisciplines, scopedLines, type ScopedLine } from "../lib/rates";
import type { Stage, StageKey, Project } from "../lib/types";

/* What each project stage actually involves — the detail behind the
   "you are here" timeline, for the studio and the client alike. */

export interface StageDetailContent { purpose: string; activities: ScopedLine[]; outputs: ScopedLine[]; client: string; }

export const STAGE_DETAIL: Record<StageKey, StageDetailContent> = {
  engagement: {
    purpose: "Agree the appointment — what we'll do, what it costs, and the terms we work under.",
    activities: ["Confirm the scope of services discipline by discipline", "Prepare the fee against your construction budget", "Set out the stages, programme and payment schedule", "Agree the limitations and terms of appointment"],
    outputs: ["Letter of engagement to sign", "Fee proposal broken down by discipline and stage", "Schedule of additional services and their rates"],
    client: "Review the engagement and sign it — that's what starts the work.",
  },
  brief: {
    purpose: "Pin down what you actually need, so everything after it is designed to the right target.",
    activities: ["Work through your objectives, priorities and constraints", "Build the accommodation schedule space by space", "Test the areas against your budget and the site", "Agree the brief before any design begins"],
    outputs: ["The written design brief", "Accommodation schedule with an area per space", "Agreed references and design direction"],
    client: "Tell us what you need, shape the spaces, and approve the brief.",
  },
  concept: {
    purpose: "Establish the architectural idea and prove the accommodation works on the site, within the budget.",
    activities: ["Site appraisal — orientation, access, levels and constraints", "Massing and spatial planning options, compared and narrowed", "Test-fit the accommodation against the brief", { text: "Preliminary structural strategy", needs: "structural" }, { text: "Preliminary building services strategy", needs: "mep" }],
    outputs: ["Concept plans, sections and elevations", "Massing model and 3D studies of the key spaces", "Design statement and area schedule"],
    client: "Review the direction and sign it off — this is the cheapest point to change course.",
  },
  developed: {
    purpose: "Resolve the signed-off concept into a coordinated design, with every space, material and system fixed.",
    activities: ["Develop each space in plan, section and elevation", "Fix materials, finishes and the key joinery", { text: "Coordinate the structural design through the building", needs: "structural" }, { text: "Coordinate the building services through the design", needs: "mep" }, "Update the cost plan against the developed design"],
    outputs: ["Developed drawings across every space", "Outline specification and finishes schedule", { text: "Coordinated structural layouts", needs: "structural" }, { text: "Coordinated MEP layouts", needs: "mep" }],
    client: "Approve the layouts, materials and finishes.",
  },
  presentation: {
    purpose: "Walk you through the resolved design space by space, and capture your decisions properly.",
    activities: ["Present the scheme with drawings and visuals", "Take your comments space by space", "Record approvals and any requested revisions", "Fold the agreed changes back into the design"],
    outputs: ["Presentation set with visuals per space", "A written record of your approvals and comments", "Revised drawings where changes were agreed"],
    client: "Review each space and approve it, or tell us what to change.",
  },
  documentation: {
    purpose: "Produce the technical set a contractor can price and build from without ambiguity.",
    activities: ["Full technical drawing production", "Construction details, junctions and assemblies", "Schedules — doors, windows, finishes and joinery", "Specification writing", { text: "Final inter-disciplinary coordination", needs: "any-consultant" }],
    outputs: ["Issued-for-Tender architectural set", { text: "Structural drawings and schedules", needs: "structural" }, { text: "MEP drawings and schedules", needs: "mep" }, "Full specification", "Schedules and detail sheets"],
    client: "Little input needed — this is the studio's production stage.",
  },
  construction: {
    purpose: "Protect the design on site — that what gets built is what was drawn, to the quality specified.",
    activities: ["Periodic site inspections against the issued drawings", "Answer contractor RFIs and issue clarification sketches", "Review shop drawings, samples and material submittals", "Instruct and record variations, and report progress to you"],
    outputs: ["Site inspection reports with photographs", "RFI responses and clarification sketches", "Reviewed shop drawings and approved samples", "Variation instructions and progress reports"],
    client: "Keep site decisions timely — a held decision on site costs more than one held at the desk.",
  },
  delivery: {
    purpose: "Take the works to market, then see them through completion and handover.",
    activities: ["Issue the tender package and manage the query process", "Review returns and recommend a contractor", "Snagging and defects inspection at completion", "Assemble the handover documentation"],
    outputs: ["Tender report and recommendation", "Snagging list and defects schedule", "Handover documentation and as-built record"],
    client: "Choose the contractor with our recommendation, then walk the snagging with us.",
  },
};

/** Programme tasks are tagged with a phase name; drawings with a register phase. */
const TASK_PHASE: Partial<Record<StageKey, string>> = {
  engagement: "Engagement", concept: "Concept", developed: "Developed",
  presentation: "Presentation", documentation: "Documentation", construction: "Construction",
};
const DRAWING_PHASE: Partial<Record<StageKey, string>> = {
  concept: "Conceptual", developed: "Design Development", documentation: "IFT",
};

/* ---------------------------------------------------------- live state ----
   "Complete / In progress / Still to come" says where a stage sits in the
   sequence, not what it's actually waiting on. This reads the real data —
   who holds it, and what they owe — so a stage can say "awaiting your
   signature" or "2 designs to review" rather than just "in progress". */

export type StateTone = "ok" | "wait" | "warn" | "idle";
export interface StageState { label: string; tone: StateTone; }

export const TONE_CLASS: Record<StateTone, string> = { ok: "badge ok", wait: "badge accent", warn: "badge warn", idle: "badge" };

export const SEQ_LABEL: Record<Stage["status"], string> = { done: "Complete", active: "In progress", upcoming: "Still to come" };
const SEQUENCE: Record<Stage["status"], StageState> = {
  done: { label: SEQ_LABEL.done, tone: "ok" },
  active: { label: SEQ_LABEL.active, tone: "wait" },
  upcoming: { label: SEQ_LABEL.upcoming, tone: "idle" },
};

/** `you` — whose portal this is, so the copy reads "your" vs "the client's". */
export function stageState(p: Project, stage: Stage, you: "studio" | "client" = "client"): StageState {
  const yours = you === "client";
  const k = stage.key;

  if (k === "engagement") {
    const s = p.engagement?.status;
    if (s === "signed") return { label: "Accepted", tone: "ok" };
    if (s === "issued") return { label: yours ? "Awaiting your signature" : "Awaiting client signature", tone: "wait" };
    return { label: "Ready to sign", tone: "wait" };
  }

  if (k === "brief") {
    const s = p.brief?.status;
    if (s === "approved") return { label: "Approved", tone: "ok" };
    if (s === "sent") return { label: yours ? "Awaiting your approval" : "With the client for approval", tone: "wait" };
    if (s === "changes") return { label: "Changes requested", tone: "warn" };
    return { label: "Being written", tone: "idle" };
  }

  if (k === "concept") {
    const boards = (p.concepts ?? []).length;
    const imgs = (p.moodboard?.images ?? []).length;
    if (stage.status === "done") return { label: "Signed off", tone: "ok" };
    if (boards || imgs) return { label: `Direction forming · ${boards || imgs} board${(boards || imgs) > 1 ? "s" : ""}`, tone: "wait" };
    return SEQUENCE[stage.status];
  }

  /* Developed design reports on its own drawings — not on the presentation's
     review state, which belongs to the next stage. */
  if (k === "developed") {
    const set = (p.deliverables ?? []).filter((d) => d.phase === "Design Development");
    if (set.length) {
      const issued = set.filter((d) => d.status === "issued").length;
      const doing = set.filter((d) => d.status === "doing").length;
      if (issued === set.length) return { label: "Design issued", tone: "ok" };
      if (issued) return { label: `${issued} of ${set.length} drawings issued`, tone: "wait" };
      if (doing) return { label: `${doing} in progress`, tone: "wait" };
    }
    return SEQUENCE[stage.status];
  }

  if (k === "presentation") {
    const spaces = p.presentation?.spaces ?? [];
    if (spaces.length) {
      const pending = spaces.filter((s) => s.response === "pending").length;
      const revise = spaces.filter((s) => s.response === "revise").length;
      const ok = spaces.filter((s) => s.response === "approved").length;
      if (revise) return { label: `${revise} revision${revise > 1 ? "s" : ""} requested`, tone: "warn" };
      if (pending) return { label: yours ? `${pending} awaiting your review` : `${pending} awaiting client review`, tone: "wait" };
      if (ok === spaces.length) return { label: "All spaces approved", tone: "ok" };
    }
    // an open drawing review the client can see means it's actively in review
    const open = (p.reviews ?? []).filter((r) => r.status === "open" && (!yours || r.sharedWithClient)).length;
    if (open) return { label: "In review", tone: "wait" };
    return SEQUENCE[stage.status];
  }

  if (k === "documentation") {
    const set = (p.deliverables ?? []).filter((d) => d.phase === "IFT");
    if (set.length) {
      const issued = set.filter((d) => d.status === "issued").length;
      if (issued === set.length) return { label: "Issued for tender", tone: "ok" };
      if (issued) return { label: `${issued} of ${set.length} sheets issued`, tone: "wait" };
      const doing = set.filter((d) => d.status === "doing").length;
      if (doing) return { label: `${doing} in production`, tone: "wait" };
    }
    return SEQUENCE[stage.status];
  }

  if (k === "delivery") {
    if (p.links?.bid) return { label: "Out to tender", tone: "wait" };
    if (p.links?.pm) return { label: "On site", tone: "wait" };
    return SEQUENCE[stage.status];
  }

  return SEQUENCE[stage.status];
}

export function StageLightbox({ stage, index, p, you = "client", onClose }: {
  stage: Stage | null; index: number; p: Project; you?: "studio" | "client"; onClose: () => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const open = !!stage;
  const key = stage?.key;
  useEffect(() => {
    if (!open) return;
    const ctx = gsap.context(() => {
      gsap.from(".sd-el", { y: 14, opacity: 0, duration: 0.45, ease: "power3.out", stagger: 0.06 });
    }, root);
    return () => ctx.revert();
  }, [open, key]);
  if (!stage) return null;

  const d = STAGE_DETAIL[stage.key];
  /* Never promise a discipline the client didn't appoint — a client who engaged
     us for architecture alone should not read that we'll do their structure. */
  const engaged = engagedDisciplines(p);
  const activities = d ? scopedLines(d.activities, engaged) : [];
  const outputs = d ? scopedLines(d.outputs, engaged) : [];
  const state = stageState(p, stage, you);
  const tasks = (p.program ?? []).filter((t) => t.phase === TASK_PHASE[stage.key]);
  const drawings = (p.deliverables ?? []).filter((x) => x.phase === DRAWING_PHASE[stage.key]);
  const doneTasks = tasks.filter((t) => t.status === "done").length;
  const issued = drawings.filter((x) => x.status === "issued").length;
  const tone = state.tone === "ok" ? "var(--ok)" : state.tone === "warn" ? "var(--warn)" : state.tone === "wait" ? "var(--accent)" : "var(--ink-3)";

  return (
    <Modal open={open} onClose={onClose} wide>
      <div ref={root}>
        <div className="sd-el row between wrap" style={{ gap: 12 }}>
          <div className="row" style={{ gap: 14 }}>
            <span className="serif" style={{ fontSize: 34, color: tone, lineHeight: 1 }}>{String(index + 1).padStart(2, "0")}</span>
            <div>
              <h3 className="serif" style={{ fontSize: 25, lineHeight: 1.15 }}>{stage.label}</h3>
              <div className="row wrap" style={{ gap: 6, marginTop: 5 }}>
                <span className={TONE_CLASS[state.tone]} style={{ fontSize: 9 }}>
                  {state.tone === "wait" && <span className="dot" />}{state.label}
                </span>
                {state.label !== SEQ_LABEL[stage.status] && <span className="dim" style={{ fontSize: 11 }}>{SEQ_LABEL[stage.status]}</span>}
                {stage.startedOn && <span className="dim" style={{ fontSize: 11.5 }}>· Started {fmtDate(stage.startedOn)}</span>}
                {stage.completedOn && <span className="dim" style={{ fontSize: 11.5 }}>· Completed {fmtDate(stage.completedOn)}</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="divider" style={{ margin: "16px 0" }} />

        <p className="sd-el muted" style={{ fontSize: 14.5, lineHeight: 1.65 }}>{d.purpose}</p>

        <div className="sd-el sd-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22, marginTop: 18 }}>
          <div>
            <div className="kicker" style={{ marginBottom: 8 }}>What we do</div>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {activities.map((x) => <li key={x} style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 4 }}>{x}</li>)}
            </ul>
          </div>
          <div>
            <div className="kicker" style={{ marginBottom: 8 }}>What you receive</div>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {outputs.map((x) => <li key={x} style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 4 }}>{x}</li>)}
            </ul>
          </div>
        </div>

        <div className="sd-el panel" style={{ padding: "12px 14px", marginTop: 16, borderLeft: `3px solid ${tone}` }}>
          <div className="kicker" style={{ fontSize: 9.5, marginBottom: 3 }}>Your part in it</div>
          <div style={{ fontSize: 13.5, lineHeight: 1.55 }}>{d.client}</div>
        </div>

        {tasks.length > 0 && (
          <div className="sd-el" style={{ marginTop: 22 }}>
            <div className="row between" style={{ marginBottom: 10 }}>
              <div className="kicker">Work in this stage</div>
              <span className="dim" style={{ fontSize: 12 }}>{doneTasks} of {tasks.length} complete</span>
            </div>
            <div className="card" style={{ overflow: "hidden" }}>
              {tasks.map((t, i) => (
                <div key={t.id} className="row between" style={{ padding: "9px 13px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
                  <span className="row" style={{ gap: 9, minWidth: 0 }}>
                    <span style={{ width: 16, height: 16, borderRadius: 16, flexShrink: 0, display: "grid", placeItems: "center", background: t.status === "done" ? "var(--ok)" : "transparent", border: "1.5px solid " + (t.status === "done" ? "var(--ok)" : t.status === "blocked" ? "var(--warn)" : "var(--line-2)"), color: "#fff" }}>
                      {t.status === "done" && <Icon.check width={10} />}
                    </span>
                    <span style={{ fontSize: 13, textDecoration: t.status === "done" ? "line-through" : "none", color: t.status === "done" ? "var(--ink-3)" : "var(--ink)" }}>{t.name}</span>
                  </span>
                  {t.status === "doing" && <span className="badge accent" style={{ fontSize: 9, flexShrink: 0 }}>{t.progress}%</span>}
                  {t.status === "blocked" && <span className="badge warn" style={{ fontSize: 9, flexShrink: 0 }}>held</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {drawings.length > 0 && (
          <div className="sd-el" style={{ marginTop: 20 }}>
            <div className="row between" style={{ marginBottom: 10 }}>
              <div className="kicker">Drawings from this stage</div>
              <span className="dim" style={{ fontSize: 12 }}>{issued} of {drawings.length} issued</span>
            </div>
            <div className="row wrap" style={{ gap: 6 }}>
              {drawings.map((x) => (
                <span key={x.id} className="badge" style={{ fontSize: 10.5, opacity: x.status === "issued" ? 1 : 0.55 }}>
                  <span className="mono">{x.code}</span> {x.title}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <style>{`@media (max-width:640px){ .sd-grid{ grid-template-columns:1fr !important } }`}</style>
    </Modal>
  );
}
